import {jwtDecode} from 'jwt-decode'

import {logger} from '#/logger'
import {isWeb} from '#/platform/detection'
import {type SessionAccount} from './types'

const COOKIE_ACCESS_TOKEN = 'wsocial_at'
const COOKIE_REFRESH_TOKEN = 'wsocial_rt'
const COOKIE_SESSION_DID = 'wsocial_did'
const COOKIE_SESSION_HANDLE = 'wsocial_handle'
const COOKIE_SESSION_SERVICE = 'wsocial_service'

const COOKIE_PATH = '/'
const COOKIE_SAME_SITE = 'Strict'
const COOKIE_SECURE =
  typeof location !== 'undefined' && location.protocol === 'https:'

function getTokenExpiry(jwt: string): Date | undefined {
  try {
    const payload = jwtDecode(jwt)
    if (payload.exp) {
      return new Date(payload.exp * 1000)
    }
  } catch {
    logger.warn('session:cookies: could not decode jwt for expiry')
  }
  return undefined
}

function setCookie(name: string, value: string, expires?: Date) {
  let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`
  cookie += `; path=${COOKIE_PATH}`
  cookie += `; SameSite=${COOKIE_SAME_SITE}`
  if (COOKIE_SECURE) {
    cookie += '; Secure'
  }
  if (expires) {
    cookie += `; expires=${expires.toUTCString()}`
  }
  document.cookie = cookie
}

function deleteCookie(name: string) {
  document.cookie = `${encodeURIComponent(name)}=; path=${COOKIE_PATH}; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=${COOKIE_SAME_SITE}${COOKIE_SECURE ? '; Secure' : ''}`
}

function getCookie(name: string): string | undefined {
  const encoded = encodeURIComponent(name) + '='
  const parts = document.cookie.split('; ')
  for (const part of parts) {
    if (part.startsWith(encoded)) {
      return decodeURIComponent(part.slice(encoded.length))
    }
  }
  return undefined
}

/**
 * Persists minimal session data (tokens + DID) into cookies whose
 * expiry matches the token lifetime. Call this whenever tokens change.
 */
export function syncSessionCookies(account: SessionAccount | undefined) {
  if (!isWeb) return

  if (!account?.accessJwt || !account?.refreshJwt) {
    clearSessionCookies()
    return
  }

  try {
    const accessExpiry = getTokenExpiry(account.accessJwt)
    const refreshExpiry = getTokenExpiry(account.refreshJwt)

    setCookie(COOKIE_ACCESS_TOKEN, account.accessJwt, accessExpiry)
    setCookie(COOKIE_REFRESH_TOKEN, account.refreshJwt, refreshExpiry)
    setCookie(COOKIE_SESSION_DID, account.did, refreshExpiry)
    setCookie(COOKIE_SESSION_HANDLE, account.handle, refreshExpiry)
    setCookie(COOKIE_SESSION_SERVICE, account.service, refreshExpiry)
  } catch (e) {
    logger.error('session:cookies: failed to sync cookies', {message: e})
  }
}

/**
 * Removes all session cookies. Call on logout.
 */
export function clearSessionCookies() {
  if (!isWeb) return

  try {
    deleteCookie(COOKIE_ACCESS_TOKEN)
    deleteCookie(COOKIE_REFRESH_TOKEN)
    deleteCookie(COOKIE_SESSION_DID)
  } catch (e) {
    logger.error('session:cookies: failed to clear cookies', {message: e})
  }
}

/**
 * Reads the minimal session tokens stored in cookies.
 * Useful for SSR or middleware that needs to verify auth state.
 */
export function readSessionCookies(): {
  accessJwt: string | undefined
  refreshJwt: string | undefined
  did: string | undefined
} {
  if (!isWeb) {
    return {accessJwt: undefined, refreshJwt: undefined, did: undefined}
  }

  try {
    return {
      accessJwt: getCookie(COOKIE_ACCESS_TOKEN),
      refreshJwt: getCookie(COOKIE_REFRESH_TOKEN),
      did: getCookie(COOKIE_SESSION_DID),
    }
  } catch (e) {
    logger.error('session:cookies: failed to read cookies', {message: e})
    return {accessJwt: undefined, refreshJwt: undefined, did: undefined}
  }
}
